import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LoyaltyBurnSliderBase from 'loyalty/components/LoyaltyBurnSliderBase'
import useLoyaltyBurnExperiment from 'loyalty/hooks/useLoyaltyBurnExperiment'
import useOfferBurnSliderState from 'loyalty/hooks/useOfferBurnSliderState'
import { CLEAR_SEARCH_BURN_BATCH_CALCULATIONS } from 'actions/actionConstants'

interface Props {
  isLoyaltyEnabled: boolean
  InnerWrapper?: React.ComponentType<{ children?: React.ReactNode }>
}

function OfferPageLoyaltyPointsSlider(props: Props) {
  const {
    isLoyaltyEnabled,
    InnerWrapper = React.Fragment,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()
  const { charterBundleEnabled } = useContext(OfferPageStateContext)
  const { isAnyVariant: isBurnEnabled } = useLoyaltyBurnExperiment()

  useEffect(() => {
    dispatch({ type: CLEAR_SEARCH_BURN_BATCH_CALCULATIONS })
  }, [dispatch])

  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const batchCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations ?? {})
  const maxBurnPoints = useMemo(() => {
    const maxPriceInPoints = Object.values(batchCalculations)
      .reduce((max, result) => Math.max(max, result?.data?.priceInPoints ?? 0), 0)
    return maxPriceInPoints > 0 ? Math.min(userPoints, maxPriceInPoints) : userPoints
  }, [batchCalculations, userPoints])

  const isLoadingMax = Object.values(batchCalculations).some(c => c?.fetching)

  const onPointsToApplyChange = useCallback((pointsToApply: number | undefined) => {
    offerPageDispatch({ type: OfferPageActions.setLoyaltyPointsToApply, pointsToApply })
  }, [offerPageDispatch])

  const { shouldShow, sliderProps } = useOfferBurnSliderState({
    isLoyaltyEnabled: isLoyaltyEnabled && isBurnEnabled,
    maxBurnPoints,
    isLoadingMax,
    onPointsToApplyChange,
    disabled: charterBundleEnabled,
  })

  if (!shouldShow) {
    return null
  }

  const sliderContent = <LoyaltyBurnSliderBase {...sliderProps} />

  return (
    <div data-large-gap="">
      <InnerWrapper>{sliderContent}</InnerWrapper>
    </div>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  isLoyaltyEnabled: getIsLoyaltyEnabled(appState),
}), OfferPageLoyaltyPointsSlider)
